/**
 * Coach identity and credentials.
 *
 * Only client-supplied qualifications are asserted. Other experience values
 * remain placeholders, and the UI suppresses anything still unresolved.
 */

import { placeholder } from './placeholders.js';

export interface Credential {
  /** Stable id, also used as the 3D credential-object key. */
  id: string;
  /** Short technical annotation shown beside the 3D object. */
  label: string;
  /** The value; suppressed by the UI while it remains a placeholder. */
  value: string;
  /** Which experience surfaces this credential. */
  surfaces: readonly ('master' | 'personal-training' | 'boxing')[];
  /** Registry key of the 3D object that represents it. */
  assetKey: string;
}

const FITNESS_QUALIFICATION = 'Certificate III in Fitness';

export const coachCard = {
  title: 'Personal trainer and combat coach',
  email: 'zach.jones@ircf.com.au',
  instagramHandle: '@zach.jones98',
  instagramUrl: 'https://www.instagram.com/zach.jones98/',
} as const;

/**
 * Credential objects arranged as a premium product installation, per the brief.
 * Each `value` stays a placeholder until verified - no fabricated statistics.
 */
export const credentials: readonly Credential[] = [
  {
    id: 'years-coaching',
    label: 'Years coaching',
    value: placeholder('COACH EXPERIENCE'),
    surfaces: ['master', 'personal-training', 'boxing'],
    assetKey: 'pt.stopwatch',
  },
  {
    id: 'qualifications',
    label: 'Qualifications and certifications',
    value: FITNESS_QUALIFICATION,
    surfaces: ['master', 'personal-training'],
    assetKey: 'pt.training-journal',
  },
  {
    id: 'strength-conditioning',
    label: 'Strength and conditioning',
    value: placeholder('COACH PERSONAL TRAINING HISTORY'),
    surfaces: ['master', 'personal-training'],
    assetKey: 'pt.barbell-collar',
  },
  {
    id: 'boxing-experience',
    label: 'Boxing experience',
    value: placeholder('COACH BOXING HISTORY'),
    surfaces: ['master', 'boxing'],
    assetKey: 'boxing.gloves',
  },
  {
    id: 'nutrition-scope',
    label: 'Nutrition coaching scope',
    value: placeholder('COACH NUTRITION SCOPE'),
    surfaces: ['master', 'personal-training'],
    assetKey: 'pt.nutrition-dial',
  },
  {
    id: 'conditioning',
    label: 'Cardiovascular conditioning',
    value: placeholder('COACH EXPERIENCE'),
    surfaces: ['master', 'personal-training'],
    assetKey: 'pt.heart-rate-monitor',
  },
  {
    id: 'delivery-modes',
    label: 'Online and hybrid coaching',
    value: placeholder('COACH EXPERIENCE'),
    surfaces: ['master', 'personal-training'],
    assetKey: 'pt.training-tablet',
  },
  {
    id: 'locations',
    label: 'Training locations',
    value: placeholder('PRIMARY LOCATION'),
    surfaces: ['master', 'boxing'],
    assetKey: 'master.location-marker',
  },
];

export type CoachingFormat = 'in-person' | 'online' | 'hybrid';

export const coachingFormats: readonly { id: CoachingFormat; label: string; description: string }[] =
  [
    {
      id: 'in-person',
      label: 'In person',
      description: 'One-to-one coaching delivered at a training location.',
    },
    {
      id: 'online',
      label: 'Online',
      description: 'Personalised programming with structured remote check-ins and support.',
    },
    {
      id: 'hybrid',
      label: 'Hybrid',
      description: 'A combination of in-person sessions and remote programming.',
    },
  ];

export const coach = {
  name: 'Zach Jones',
  monogram: 'ZJ',

  biography:
    'Zach Jones knows progress is rarely a straight line. Training and competition have tested him with hard rounds, setbacks and the choice to return to the work. In the cage, resolve means staying composed under pressure; outside it, rebuilding, learning and showing up again with purpose.\n\nThat experience defines his coaching. Zach does not promise shortcuts or demand perfection. He is passionate about helping each client create meaningful change, recognise what they are capable of and build the resolve to reach their full potential. Iron Resolve is the standard: adapt, persist and come back stronger.',
  qualifications: FITNESS_QUALIFICATION,
  experience: placeholder('COACH EXPERIENCE'),
  boxingHistory: placeholder('COACH BOXING HISTORY'),
  personalTrainingHistory: placeholder('COACH PERSONAL TRAINING HISTORY'),
  nutritionScope: placeholder('COACH NUTRITION SCOPE'),

  /**
   * The coaching philosophy is genuinely brand-defining copy rather than a
   * client fact, so a default is provided and remains admin-editable.
   */
  philosophy: [
    'Training is only effective when it reflects the individual. Every program begins with your goals, current ability, lifestyle and preferred way of training.',
    'The process stays the same whether the goal is strength, fitness, body composition or boxing skill: assess honestly, plan deliberately, coach the detail, measure what matters and progress on evidence.',
  ],

  /** Commitments stated as method, not as outcome claims. */
  commitments: [
    'Safety first - screening, technique and load managed before intensity.',
    'Individualisation - no template applied without adjustment.',
    'Measurable progression - every change is justified by data or feedback.',
  ],

  credentials,
  coachingFormats,

  /**
   * Scope-of-practice guard. The platform must never describe the coach as a
   * dietitian unless that qualification is explicitly supplied and verified.
   */
  scopeOfPractice: {
    isRegisteredDietitian: false,
    nutritionScopeStatement:
      'Nutrition guidance is provided as general coaching education within the scope of a fitness professional. It is not medical nutrition therapy and does not replace advice from a doctor, accredited practising dietitian or other qualified health professional.',
  },

  /** Client groups coached. Placeholder until confirmed. */
  clientTypes: [placeholder('COACH EXPERIENCE')],
} as const;
